'use client';

import { useEffect, useState } from 'react';

import { useCurrentUserId } from '@ps/ui/contexts/auth';

import { useCommunityContext } from '../contexts/community/useCommunityContext';
import { subscribeToMyChannelMembership } from '../functions/members/subscribeToMyChannelMembership';
import type { CommunityChannelMember } from '../types/CommunityChannelMember';

/**
 * Live view of the current user's membership doc for a channel. The return is
 * TRI-STATE: `undefined` = still loading (auth resolving, or the channel/creator
 * not yet in scope), `null` = resolved "not a member" (the doc doesn't exist, OR
 * a confirmed-anonymous viewer who can't have one), a doc = joined. Components
 * default the missing-doc state to "not muted, mentions on" so the UI keeps
 * working before/without an explicit join.
 *
 * The anonymous → `null` distinction is load-bearing: the visitor gate
 * (`VisitorChannelDetail`) and the "Join this Channel" banner (`ChannelDetailPane`)
 * both key off `membership === null` to treat a logged-out viewer as "not joined."
 * Collapsing anon into `undefined` strands a private channel on its spinner and
 * suppresses the public-channel join banner. `useCurrentUserId()` is the lazy-auth
 * tri-state (`undefined` resolving → `null` anonymous → uid).
 */
export function useMyChannelMembership(
  channelId: string | null | undefined,
): CommunityChannelMember | null | undefined {
  const { creatorId } = useCommunityContext();
  const currentUserId = useCurrentUserId();
  const [member, setMember] = useState<
    CommunityChannelMember | null | undefined
  >(undefined);

  useEffect(() => {
    // Auth still resolving, or the channel/creator isn't in scope yet — genuine
    // loading, keep `undefined`.
    if (currentUserId === undefined || !creatorId || !channelId) {
      setMember(undefined);
      return;
    }
    // Resolved anonymous: no membership is possible, so this is a definite
    // "not a member" (`null`), NOT a loading state.
    if (currentUserId === null) {
      setMember(null);
      return;
    }
    return subscribeToMyChannelMembership(
      creatorId,
      channelId,
      currentUserId,
      setMember,
    );
  }, [creatorId, currentUserId, channelId]);

  return member;
}
